import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import {
  articleLoadFailureReason,
  articleLoadFailureStatus,
  isArticleLoadFailureRetryable,
  type ArticleLoadFailureReason,
} from '../articleLoadFailure';
import { QueryKey } from '@/Config/api/queryKey';
import { ArticleDto, ArticleInputDto } from '@/Domain/api/KnowledgeBase/dto/Article.dto';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

type TagObject = { value: string };

export type ArticleForm = ArticleDto & {
  tagsObjects: TagObject[];
};

export interface UseArticleSheetReturn {
  form: ReturnType<typeof useForm<ArticleForm>>;
  article: ReturnType<typeof useQuery<ArticleDto>>;
  isArticleUnavailable: boolean;
  loadFailureReason: ArticleLoadFailureReason | null;
  isLoading: boolean;
  markLocalEdit: () => void;
  onSubmit: (values: ArticleForm) => void;
  createSubmitHandler: (options?: {
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
  }) => (values: ArticleForm) => void;
}

export const useArticleSheet = (): UseArticleSheetReturn => {
  const queryClient = useQueryClient();
  const { id } = useParams<{ id?: string }>();
  const { capture } = usePostHogAnalytics();
  const localEditStartedRef = useRef(false);

  // Article query
  const article = useQuery<ArticleDto>({
    queryKey: [QueryKey.knowledgeBase.article, id],
    staleTime: 0,
    // Retaining the body renders a stale editor: the rich-text sync only accepts a value on initial load.
    gcTime: 0,
    enabled: id != null,
    // Disable structural sharing to ensure new reference on refetch,
    // which triggers the form reset effect
    structuralSharing: false,
  });

  // Article mutation
  const articleMutation = useMutation<
    ArticleDto,
    unknown,
    { id: string } & Partial<ArticleInputDto>
  >({
    mutationKey: [QueryKey.knowledgeBase.article, id],
    onSuccess: (updatedArticle) => {
      capture(PostHogEventEnum.ArticleUpdated, {
        article_id: id,
      });
      queryClient.setQueryData([QueryKey.knowledgeBase.article, id], updatedArticle);
      void queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
      void queryClient.invalidateQueries({ queryKey: ['knowledge_base'] });
    },
  });

  // Form setup
  const form = useForm<ArticleForm>({
    defaultValues: article.data
      ? {
          ...article.data,
          tagsObjects: (article.data.tags || []).map((t: string) => ({ value: t })),
        }
      : undefined,
  });
  const isFormDirty = form.formState.isDirty;
  const markLocalEdit = useCallback(() => {
    localEditStartedRef.current = true;
  }, []);

  // Track local changes - handled by consumer via form.formState

  // Keep form in sync when article loads/changes
  useLayoutEffect(() => {
    if (!article.data || isFormDirty || localEditStartedRef.current) return;
    form.reset({
      ...article.data,
      tagsObjects: (article.data.tags || []).map((t: string) => ({ value: t })),
    });
  }, [article.data, form, isFormDirty]);

  // Submit handler (base)
  const baseSubmit = useCallback(
    (
      values: ArticleForm,
      callbacks?: { onSuccess?: () => void; onError?: (error: unknown) => void },
    ) => {
      const cleanedTags = (values.tagsObjects || [])
        .map((t) => (t.value ?? '').trim())
        .filter((v) => v.length > 0);

      const {
        id: _id,
        created_timestamp: _created,
        downloaded_timestamp: _downloaded,
        last_updated_timestamp: _lastUpdated,
        last_updated_from_external_source_timestamp: _lastUpdatedExternal,
        content_edited_manually_at: _contentEditedManuallyAt,
        state: _state,
        type: _type,
        tagsObjects: _tagsObjects,
        ...rest
      } = values;

      const payload: Partial<ArticleInputDto> = {
        ...rest,
        tags: cleanedTags,
      };
      // Send content/title only when the user actually edited them: the
      // markdown editor normalizes content on load, so a non-dirty value can
      // differ from the DB row and would read as a manual edit server-side.
      const { dirtyFields } = form.formState;
      if (!dirtyFields.content) delete payload.content;
      if (!dirtyFields.title) delete payload.title;
      // Resending an unchanged list would overwrite tags background auto-tagging
      // wrote after this form loaded. `values.tags` is the baseline, kept in lockstep
      // by Article.sheet's AUTO_TAGGED handler; compared by value because RHF's
      // useFieldArray dirty tracking can leave a truthy all-false array.
      const baselineTags = values.tags ?? [];
      const tagsUnchanged =
        cleanedTags.length === baselineTags.length &&
        cleanedTags.every((tag, index) => tag === baselineTags[index]);
      if (tagsUnchanged) delete payload.tags;

      if (!id) {
        callbacks?.onError?.(new Error('Missing article id'));
        return;
      }
      articleMutation.mutate(
        { id, ...payload },
        {
          onSuccess: (updatedArticle) => {
            localEditStartedRef.current = false;
            form.reset({
              ...updatedArticle,
              tagsObjects: (updatedArticle.tags || []).map((value) => ({ value })),
            });
            callbacks?.onSuccess?.();
          },
          onError: (error) => {
            callbacks?.onError?.(error);
          },
        },
      );
    },
    [articleMutation, form],
  );

  // Public submit variants
  const onSubmit = useCallback((values: ArticleForm) => baseSubmit(values), [baseSubmit]);
  const createSubmitHandler = useCallback(
    (options?: { onSuccess?: () => void; onError?: (error: unknown) => void }) =>
      (values: ArticleForm) =>
        baseSubmit(values, options),
    [baseSubmit],
  );

  const loadFailureReason = article.isError ? articleLoadFailureReason(article.error) : null;
  const loadFailureStatus = article.isError ? articleLoadFailureStatus(article.error) : null;
  // A 4xx is settled before the request leaves the browser, so a previous successful load's
  // data must not keep the editor alive over it: the server will not accept that row back.
  const hasDeterministicLoadError =
    loadFailureReason !== null && !isArticleLoadFailureRetryable(loadFailureReason);
  const isArticleUnavailable = article.isError && (!article.data || hasDeterministicLoadError);
  const isLoading = isArticleUnavailable || !article.data || articleMutation.isPending;

  // React Query catches the queryFn rejection, so PostHog's capture_exceptions -- which only
  // sees uncaught errors -- never fired for this. A user hit a permanently failing article
  // sheet in production and we could see the rageclicks but not the status code.
  //
  // `status_code` rides along with the bounded `reason`, matching `article_upload_failed`:
  // the reason alone cannot separate a 404 from a 422, and that is exactly the distinction
  // the one incident we have turns on.
  //
  // Keyed on errorUpdatedAt so this is one event per failed attempt: a retry that fails the
  // same way reports again, which is the signal that a retry is not helping. Re-renders do
  // not move any dependency, so it stays one event per attempt rather than one per render.
  useEffect(() => {
    if (!loadFailureReason || !id) return;
    capture(PostHogEventEnum.ArticleLoadFailed, {
      article_id: id,
      reason: loadFailureReason,
      status_code: loadFailureStatus,
      retryable: isArticleLoadFailureRetryable(loadFailureReason),
    });
  }, [loadFailureReason, loadFailureStatus, id, capture, article.errorUpdatedAt]);

  return {
    form,
    article,
    isArticleUnavailable,
    loadFailureReason,
    isLoading,
    markLocalEdit,
    onSubmit,
    createSubmitHandler,
  };
};
